import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Button,
} from '@mui/material';
import {
  ChildCare,
  Edit,
  Warning,
  FolderOutlined,
  AccountBalanceWalletOutlined,
  ArticleOutlined,
  CardMembershipOutlined,
  StarOutline,
  AccountBoxOutlined,
} from '@mui/icons-material';
import { AppLayout } from '../components/layout/AppLayout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useProfile } from '../hooks/useProfile';
import { useClinic } from '../hooks/useClinic';
import { getUserPhotoUrl } from '../utils/getUserPhotoUrl';

interface CabinetItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  isPrimary?: boolean;
}

const ActionCard: React.FC<CabinetItem & { onClick: () => void; disabled?: boolean }> = ({
  title,
  icon,
  onClick,
  disabled,
  isPrimary,
}) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      backgroundColor: isPrimary ? '#007AFF' : '#FFFFFF',
      borderRadius: '20px',
      padding: '14px 16px',
      width: '100%',
      height: '105px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'transform 0.15s ease',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      '&:active': disabled ? undefined : { transform: 'scale(0.97)' },
      opacity: disabled ? 0.5 : 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxSizing: 'border-box',
    }}
  >
    <Typography
      sx={{
        fontSize: 15,
        fontWeight: 700,
        color: isPrimary ? '#FFFFFF' : '#212121',
        lineHeight: 1.3,
        whiteSpace: 'pre-line',
      }}
    >
      {title}
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: isPrimary ? 'rgba(255,255,255,0.8)' : '#007AFF',
        }}
      >
        Перейти &gt;
      </Typography>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: isPrimary ? '#FFFFFF' : '#007AFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isPrimary ? '#007AFF' : '#FFFFFF',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    </Box>
  </Box>
);

export const CabinetPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { childrenBalances, features } = useProfile();
  const { branches } = useClinic();
  const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    const photoUrl = getUserPhotoUrl();
    setUserPhotoUrl(photoUrl);
  }, []);

  const hasPatientCard = !!user?.patientClinicId;
  const hasChildren = childrenBalances.length > 0;

  // Находим текущий филиал
  const currentBranch = branches.find(b => b.id === user?.branchId);

  const allItems: CabinetItem[] = [
    {
      title: 'Мой\nбаланс',
      path: '/balance',
      icon: <AccountBalanceWalletOutlined sx={{ fontSize: 20 }} />,
    },
    {
      title: 'Мои\nфайлы',
      path: '/documents',
      icon: <FolderOutlined sx={{ fontSize: 20 }} />,
    },
    {
      title: 'Заказать\nсправку',
      path: '/paper-request',
      icon: <ArticleOutlined sx={{ fontSize: 20 }} />,
    },
    {
      title: 'Заказать\nсертификат',
      path: '/certificate-request',
      icon: <CardMembershipOutlined sx={{ fontSize: 20 }} />,
    },
    {
      title: 'Оставить\nотзыв',
      path: '/review',
      icon: <StarOutline sx={{ fontSize: 20 }} />,
    },
    {
      title: 'Записаться на приём',
      path: '/book-appointment',
      icon: <AccountBoxOutlined sx={{ fontSize: 20 }} />,
      isPrimary: true,
    },
  ];

  // Скрываем "Мои файлы" если у клиники включено pets: true
  const cabinetItems = allItems.filter(item => !(features.pets && item.title === 'Мои\nфайлы'));

  const handleItemClick = (item: CabinetItem) => {
    if (item.title !== 'Оставить отзыв' && item.title !== 'Мой баланс' && item.title !== 'Записаться на приём') {
      if (!hasPatientCard) {
        navigate('/profile?tab=data');
        return;
      }
    }
    navigate(item.path);
  };

  const handleEditProfile = () => {
    navigate('/profile');
  };

  return (
    <AppLayout title="Мой кабинет">
      <Box sx={{ pb: 8 }}>
        {/* ============================================================ */}
        {/* ЗАГОЛОВОК СТРАНИЦЫ */}
        {/* ============================================================ */}
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 700,
            color: '#000',
            mb: 2,
          }}
        >
          Мой кабинет
        </Typography>

        {/* ============================================================ */}
        {/* КАРТОЧКА ПОЛЬЗОВАТЕЛЯ */}
        {/* ============================================================ */}
        <Paper
          sx={{
            mb: 1.5,
            px: 2,
            py: 0,
            borderRadius: '20px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            height: '100px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <Avatar
              src={userPhotoUrl || undefined}
              sx={{
                width: 52,
                height: 52,
                bgcolor: '#007AFF',
                fontSize: 22,
                fontWeight: 600,
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#000' }}>
                  {user?.name || 'Пользователь'}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleEditProfile}
                  sx={{
                    p: 0.4,
                    color: '#007AFF',
                    backgroundColor: 'rgba(0,122,255,0.1)',
                    borderRadius: '6px',
                    '&:hover': { backgroundColor: 'rgba(0,122,255,0.15)' },
                  }}
                >
                  <Edit sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: 13, color: '#8E8E93', mt: 0.25 }}>
                {currentBranch?.name || 'Филиал не выбран'}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#007AFF' }}>
                {user?.points?.toLocaleString() || 0} баллов
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* ============================================================ */}
        {/* СЕТКА ДЕЙСТВИЙ (2x3) */}
        {/* ============================================================ */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {cabinetItems.map((item) => {
            const requiresCard = item.title !== 'Записаться\nна приём' && item.title !== 'Оставить\nотзыв' && item.title !== 'Мой\nбаланс';
            const disabled = requiresCard && !hasPatientCard;

            return (
              <Box key={item.title} sx={{ width: 'calc(50% - 3px)' }}>
                <ActionCard
                  {...item}
                  onClick={() => handleItemClick(item)}
                  disabled={disabled}
                />
              </Box>
            );
          })}
        </Box>

        {/* ============================================================ */}
        {/* ИНФОРМАЦИОННЫЙ БЛОК (если карта не привязана) */}
        {/* ============================================================ */}
        {!hasPatientCard && (
          <Box
            sx={{
              mt: 2,
              px: 2,
              py: 1.5,
              borderRadius: '12px',
              backgroundColor: '#F5F5F5',
              display: 'flex',
              gap: 1.5,
            }}
          >
            <Warning
              sx={{
                color: '#007AFF',
                fontSize: 20,
                flexShrink: 0,
                mt: 0.25,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#1C1C1E',
                  lineHeight: 1.5,
                  mb: 0.75,
                }}
              >
                Для доступа к записям, файлам и заказу справок необходимо сопоставить карту клиники
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: '#6C6C70',
                  lineHeight: 1.5,
                }}
              >
                Пожалуйста, обратитесь к менеджеру. Для этого закройте мини-приложение и нажмите «Чат с менеджером» в боте.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </AppLayout>
  );
};